import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  deleteDoc,
  collectionGroup,
  increment,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";

const provider = new GoogleAuthProvider();
const Oprovider = new OAuthProvider("apple.com"); // Specify 'apple.com' as the provider ID
const storage = getStorage();
// Don't store auth.currentUser in a variable as it won't update when user logs in/out

const signup = async (email, password, firstName, lastName, mobile, countryCode = "+1") => {
  // 🔹 Validate inputs
  if (!email || typeof email !== "string" || !email.trim()) {
    return {
      success: false,
      error: "invalid-input",
      message: "Email is required",
    };
  }

  if (!password || typeof password !== "string" || password.length < 6) {
    return {
      success: false,
      error: "invalid-input",
      message: "Password must be at least 6 characters",
    };
  }

  if (!firstName || typeof firstName !== "string" || !firstName.trim()) {
    return {
      success: false,
      error: "invalid-input",
      message: "First name is required",
    };
  }

  if (!lastName || typeof lastName !== "string" || !lastName.trim()) {
    return {
      success: false,
      error: "invalid-input",
      message: "Last name is required",
    };
  }

  // 🔹 Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return {
      success: false,
      error: "invalid-email",
      message: "Invalid email format",
    };
  }

  // 🔹 Validate mobile number (U.S. 10-digit)
  if (!mobile || typeof mobile !== "string" || !mobile.trim()) {
    return {
      success: false,
      error: "invalid-input",
      message: "Mobile number is required",
    };
  }

  const mobileDigits = mobile.replace(/\D/g, ""); // remove non-digits
  if (!/^\d{10}$/.test(mobileDigits)) {
    return {
      success: false,
      error: "invalid-mobile",
      message: "Please enter a valid 10-digit U.S. mobile number.",
    };
  }

  // 🔹 Remove formatting step that concatenates the numbers
  // Just use mobileDigits and countryCode directly

  try {
    // ✅ Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email.trim(),
      password
    );
    const user = userCredential.user;

    // ✅ Store additional user details in Firestore
    await setDoc(doc(db, "vendorUsers", user.uid), {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      mobile: mobileDigits,
      countryCode: countryCode,
      userRole: "vendor",
      createdAt: new Date(),
    });

    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        mobile: mobileDigits,
        countryCode: countryCode,
      },
    };
  } catch (error) {
    console.error("Firebase signup error:", error.code, error.message);

    let errorMessage = error.message || "An error occurred during signup.";

    switch (error.code) {
      case "auth/invalid-email":
        errorMessage = "The email address is not valid.";
        break;
      case "auth/email-already-in-use":
        errorMessage = "This email is already registered.";
        break;
      case "auth/weak-password":
        errorMessage = "The password is too weak (minimum 6 characters).";
        break;
      case "auth/network-request-failed":
        errorMessage = "Network error. Please check your connection.";
        break;
      case "auth/too-many-requests":
        errorMessage = "Too many attempts. Please try again later.";
        break;
      case "firestore/permission-denied":
        errorMessage = "Permission denied while writing to Firestore.";
        break;
      default:
        errorMessage =
          error.message || "An unknown error occurred during signup.";
        break;
    }

    return {
      success: false,
      error: error.code || "unknown",
      message: errorMessage,
    };
  }
};

const loginWithGoogle = async (setErrors, setLoading, navigate) => {
  setLoading(true);
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    await setDoc(doc(db, "vendorUsers", user.uid), {
      firstName: user.displayName?.split(" ")[0] || "",
      lastName: user.displayName?.split(" ")[1] || "",
      email: user.email,
      userRole: "vendor",
      createdAt: new Date(),
    });

    navigate("/account-setup");
  } catch (error) {
    setErrors({ general: error.message || "Failed to sign up with Google." });
  } finally {
    setLoading(false);
  }
};

const loginWithApple = async (setErrors, setLoading, navigate) => {
  setLoading(true);
  try {
    const result = await signInWithPopup(auth, Oprovider);
    const user = result.user;

    await setDoc(doc(db, "vendorUsers", user.uid), {
      firstName: user.displayName?.split(" ")[0] || "",
      lastName: user.displayName?.split(" ")[1] || "",
      email: user.email,
      userRole: "vendor",
      createdAt: new Date(),
    });

    navigate("/account-setup");
  } catch (error) {
    setErrors({ general: error.message || "Failed to sign up with Apple." });
  } finally {
    setLoading(false);
  }
};

const completeAccountSetup = async (
  formData,
  currentUser,
  setError,
  setLoading,
  navigate,
  setUploadProgress = null
) => {
  setLoading(true);
  setError("");

  try {
    console.log("Starting account setup...");
    console.log("FormData:", formData);
    console.log("Current User:", currentUser);

    // Verify user is authenticated
    if (!currentUser || !currentUser.uid) {
      throw new Error("User not authenticated");
    }

    const storage = getStorage();

    // Reduce retry times to fail faster and provide better error handling
    storage.maxUploadRetryTime = 60000; // 1 minute
    storage.maxOperationRetryTime = 60000; // 1 minute

    let logoUrl = null;
    let logoPath = null;
    let profilePictureUrl = null;
    let profilePicturePath = null;
    let additionalImageUrls = [];
    let additionalImagePaths = [];

    // Helper function for file upload with better CORS error handling
    const uploadFile = async (file, path, description = "file") => {
      return new Promise((resolve, reject) => {
        try {
          console.log(`Starting upload for ${description} to path: ${path}`);

          const storageRef = ref(storage, path);
          const uploadTask = uploadBytesResumable(storageRef, file);

          // Set a shorter timeout to detect CORS issues faster
          const timeout = setTimeout(() => {
            uploadTask.cancel();
            reject(
              new Error(
                `Upload timeout for ${description} - please check your connection and Firebase configuration`
              )
            );
          }, 120000); // 2 minutes

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload progress for ${description}: ${progress}%`);
              if (setUploadProgress) {
                setUploadProgress(
                  `Uploading ${description}: ${Math.round(progress)}%`
                );
              }
            },
            (error) => {
              clearTimeout(timeout);
              console.error(`Upload error for ${description}:`, error);

              // Handle specific Firebase Storage errors
              if (error.code === "storage/unauthorized") {
                reject(
                  new Error(
                    `Upload failed: You don't have permission to upload ${description}. Please check Firebase Storage rules.`
                  )
                );
              } else if (error.code === "storage/retry-limit-exceeded") {
                reject(
                  new Error(
                    `Upload failed: Network timeout for ${description}. Please check your internet connection and try again.`
                  )
                );
              } else if (error.code === "storage/canceled") {
                reject(new Error(`Upload canceled for ${description}`));
              } else if (error.code === "storage/invalid-format") {
                reject(new Error(`Invalid file format for ${description}`));
              } else if (error.code === "storage/quota-exceeded") {
                reject(
                  new Error("Storage quota exceeded. Please contact support.")
                );
              } else if (error.code === "storage/retry-limit-exceeded") {
                console.error("Network timeout. Check connection or CORS.");
              } else {
                reject(
                  new Error(
                    `Upload failed for ${description}: ${
                      error.message || "Unknown error"
                    }`
                  )
                );
              }
            },
            async () => {
              clearTimeout(timeout);
              try {
                console.log(
                  `Upload completed for ${description}, getting download URL...`
                );
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                console.log(
                  `Download URL obtained for ${description}:`,
                  downloadURL
                );
                resolve(downloadURL);
              } catch (error) {
                console.error(
                  `Error getting download URL for ${description}:`,
                  error
                );
                reject(
                  new Error(
                    `Failed to get download URL for ${description}: ${error.message}`
                  )
                );
              }
            }
          );
        } catch (error) {
          console.error(`Error setting up upload for ${description}:`, error);
          reject(error);
        }
      });
    };

    // Upload Logo
    if (formData.logo) {
      try {
        if (setUploadProgress) setUploadProgress("Uploading logo...");

        const timestamp = new Date().getTime();
        const fileExtension = formData.logo.name.split(".").pop();
        logoPath = `VendorImages/${currentUser.uid}/logo_${timestamp}.${fileExtension}`;

        console.log("Uploading logo to path:", logoPath);
        logoUrl = await uploadFile(formData.logo, logoPath, "logo");
        console.log("Logo uploaded successfully:", logoUrl);
      } catch (error) {
        console.error("Logo upload failed:", error);
        throw new Error(`Logo upload failed: ${error.message}`);
      }
    }

    // Upload Profile Picture
    if (formData.profilePicture) {
      try {
        if (setUploadProgress)
          setUploadProgress("Uploading profile picture...");

        const timestamp = new Date().getTime();
        const fileExtension = formData.profilePicture.name.split(".").pop();
        profilePicturePath = `VendorImages/${currentUser.uid}/profile_${timestamp}.${fileExtension}`;

        console.log("Uploading profile picture to path:", profilePicturePath);
        profilePictureUrl = await uploadFile(
          formData.profilePicture,
          profilePicturePath,
          "profile picture"
        );
        console.log(
          "Profile picture uploaded successfully:",
          profilePictureUrl
        );
      } catch (error) {
        console.error("Profile picture upload failed:", error);
        throw new Error(`Profile picture upload failed: ${error.message}`);
      }
    }

    // Upload Additional Images
    if (formData.additionalImages && formData.additionalImages.length > 0) {
      console.log(
        `Uploading ${formData.additionalImages.length} additional images...`
      );

      for (let i = 0; i < formData.additionalImages.length; i++) {
        const image = formData.additionalImages[i];
        try {
          if (setUploadProgress) {
            setUploadProgress(
              `Uploading additional image ${i + 1} of ${
                formData.additionalImages.length
              }...`
            );
          }

          const timestamp = new Date().getTime();
          const fileExtension = image.file.name.split(".").pop();
          const imagePath = `VendorImages/${currentUser.uid}/additional_${timestamp}_${i}.${fileExtension}`;

          console.log(
            `Uploading additional image ${i + 1} to path:`,
            imagePath
          );
          const imageUrl = await uploadFile(
            image.file,
            imagePath,
            `additional image ${i + 1}`
          );

          additionalImageUrls.push(imageUrl);
          additionalImagePaths.push(imagePath);

          console.log(
            `Additional image ${i + 1} uploaded successfully:`,
            imageUrl
          );
        } catch (error) {
          console.error(`Additional image ${i + 1} upload failed:`, error);
          throw new Error(
            `Additional image ${i + 1} upload failed: ${error.message}`
          );
        }
      }
    }

    // Save to Firestore
    if (setUploadProgress) setUploadProgress("Saving account information...");
    console.log("Saving to Firestore...");

    try {
      const userRef = doc(db, "vendorUsers", currentUser.uid);
      // Fetch current document to check for existing Stripe account
      const userSnap = await getDoc(userRef);
      const existingData = userSnap.exists() ? userSnap.data() : {};

      const userData = {
        mobileNo: formData.mobileNo || "",
        countryCode: formData.countryCode || "+1",
        headquarters: formData.headquarters || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        logoUrl: logoUrl,
        logoPath: logoPath,
        profilePictureUrl: profilePictureUrl,
        profilePicturePath: profilePicturePath,
        additionalImages: additionalImageUrls,
        additionalImagePaths: additionalImagePaths,
        isVerified: !!existingData.stripeAccountId,
        accountSetupCompleted: true,
        updatedAt: new Date(),
        createdAt: serverTimestamp(),
      };

      console.log("User data to save:", userData);

      await setDoc(userRef, userData, { merge: true });
      console.log("Firestore save complete");

      // Clean up object URLs to prevent memory leaks
      if (formData.logoPreview) {
        URL.revokeObjectURL(formData.logoPreview);
      }
      if (formData.profilePreview) {
        URL.revokeObjectURL(formData.profilePreview);
      }
      formData.additionalImages?.forEach((img) => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });

      if (setUploadProgress)
        setUploadProgress("Account setup complete! Redirecting...");
      console.log("Navigating to dashboard...");

      // Small delay to show success message
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (firestoreError) {
      console.error("Firestore save error:", firestoreError);
      throw new Error(`Failed to save account data: ${firestoreError.message}`);
    }
  } catch (error) {
    console.error("Setup error:", error);

    let message = "Failed to set up account. Please try again.";

    // Handle specific error types
    if (
      error.message.includes("CORS") ||
      error.message.includes("unauthorized")
    ) {
      message =
        "Upload failed due to permission issues. Please check your Firebase configuration and try again.";
    } else if (
      error.message.includes("network") ||
      error.message.includes("timeout")
    ) {
      message =
        "Upload failed due to network issues. Please check your internet connection and try again.";
    } else if (error.message.includes("quota")) {
      message = "Storage quota exceeded. Please contact support.";
    } else if (error.message) {
      message = error.message;
    }

    setError(message);
  } finally {
    setLoading(false);
    if (setUploadProgress) setUploadProgress("");
  }
};

const UserData = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.log("No user logged in");
      return null;
    }
    console.log("Fetching user data for userId:", userId);
    const docRef = doc(db, "vendorUsers", userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      // Add the uid to the returned data
      const userData = {
        ...docSnap.data(),
        uid: userId, // Ensure uid is included in the returned data
      };
      console.log("User Data:", userData);
      return userData;
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error getting document:", error);
    return null;
  }
};

export const validateStrongPassword = (password) => {
  if (!password) return "Password is required";
  if (password.length < 8) return "Password must be at least 8 characters long";
  if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
  if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
  if (!/[0-9]/.test(password)) return "Password must contain at least one number";
  if (!/[@$!%*?&]/.test(password)) return "Password must contain at least one special character (@$!%*?&)";
  return "";
};

export const changeVendorPassword = async (currentPassword, newPassword) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");

  try {
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error) {
    console.error("Change password error:", error);
    let message = "Failed to change password. Please try again.";
    
    if (error.code === "auth/invalid-credential" || error.code === "auth/wrong-password") {
      message = "Incorrect current password.";
    } else if (error.code === "auth/too-many-requests") {
      message = "Too many attempts. Please try again later.";
    } else if (error.message) {
      message = error.message;
    }
    
    throw new Error(message);
  }
};

// 🔹 Scan Gun Order Function
export const buyScanGun = async (orderDetails) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User is not authenticated");

  try {
    // 1. Add the order to a global scanGunOrders collection
    const orderRef = doc(collection(db, "scanGunOrders"));
    await setDoc(orderRef, {
      ...orderDetails,
      vendorId: user.uid,
      status: "pending",
      orderedAt: new Date(),
    });

    return { success: true, orderId: orderRef.id };
  } catch (error) {
    console.error("Error ordering scan gun:", error);
    throw error;
  }
};

export {
  signup,
  loginWithGoogle,
  loginWithApple,
  completeAccountSetup,
  UserData,
};

// Payment details updater
export const updatePaymentDetails = async (db, auth, details) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) throw new Error("User not authenticated");
    const userRef = doc(db, "vendorUsers", currentUser.uid);
    await updateDoc(userRef, {
      paymentDetails: {
        method: details.method || "",
        accountHolderName: details.accountHolderName || "",
        bankName: details.bankName || "",
        accountNumber: details.accountNumber || "",
        ifsc: details.ifsc || "",
        upiId: details.upiId || "",
        updatedAt: new Date().toISOString(),
      },
    });
    return { success: true };
  } catch (error) {
    console.error("Failed to update payment details", error);
    throw error;
  }
};
